import type { CalibrationCaptures, StepCapture } from './types';

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const round = (value: number, decimals = 6) => Number(value.toFixed(decimals));

export const numbersOnly = (values: number[]) => values.filter((value) => Number.isFinite(value));

export const average = (values: number[]) => {
  const clean = numbersOnly(values);
  if (clean.length === 0) return null;
  let sum = 0;
  for (const value of clean) sum += value;
  return sum / clean.length;
};

export const median = (values: number[]) => {
  const clean = numbersOnly(values).sort((a, b) => a - b);
  if (clean.length === 0) return null;
  const mid = Math.floor(clean.length / 2);
  return clean.length % 2 === 0 ? (clean[mid - 1] + clean[mid]) / 2 : clean[mid];
};

export const percentile = (values: number[], percentileValue: number) => {
  const clean = numbersOnly(values).sort((a, b) => a - b);
  if (clean.length === 0) return null;
  const idx = clamp((percentileValue / 100) * (clean.length - 1), 0, clean.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return clean[lo];
  const t = idx - lo;
  return clean[lo] * (1 - t) + clean[hi] * t;
};

export const flattenCaptureValues = <T extends number>(
  captures: StepCapture[],
  pick: (capture: StepCapture) => T[],
) => {
  const out: number[] = [];
  for (const capture of captures) {
    out.push(...pick(capture));
  }
  return out;
};

export const captureDecayRatio = (capture: StepCapture) => {
  const values = numbersOnly(capture.rms);
  if (values.length < 6) return null;
  const third = Math.max(2, Math.floor(values.length / 3));
  const head = median(values.slice(0, third));
  const tail = median(values.slice(values.length - third));
  if (!head || head <= 1e-8 || !tail) return null;
  return clamp(tail / head, 0.05, 1.5);
};

export const countFrames = (captures: CalibrationCaptures, current: StepCapture | null = null) => {
  const done =
    captures.noise.reduce((sum, capture) => sum + capture.rms.length, 0) +
    captures.single.reduce((sum, capture) => sum + capture.rms.length, 0) +
    captures.chord.reduce((sum, capture) => sum + capture.rms.length, 0);
  return done + (current?.rms.length ?? 0);
};
